<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable"
      :loading="loading"
      :headers="headers"
      :items="items"
      :fixed-header="true"
      :show-select="false"
      item-key="id"
      :items-length="totalItems"
      sort-asc-icon="$sortArrow"
      sort-desc-icon="$sortArrow"
      :page="pagination.page + 1"
      :items-per-page="pagination.pageSize"
      @update:options="handleOptionsUpdate"
    >
      <template #item="{ item, index }">
        <tr :key="item.id" :data-testid="`exclusions-table-row-${index}`">
          <td class="pa-4" :data-testid="`exclusions-table-path-col-${index}`">
            <div class="d-flex align-center">
              <v-icon class="mr-2" size="35" color="primary" :icon="getIcon(item.type)" />
              <div>
                <span class="caption text-indigo-medium">
                  {{ $t(`endpointDataGovernance.exclusionsTab.filters.types.${item.type}`) }}
                </span>
                <ellipsified-copy-text
                  class="body2 font-weight-bold"
                  width="450px"
                  :text="getDisplayPath(item)"
                />
                <span
                  v-if="item.timestamp"
                  class="caption text-indigo-medium"
                  :data-testid="`exclusions-table-date-${index}`"
                >
                  {{ getFormattedDateTime(item.timestamp) }}
                </span>
              </div>
            </div>
          </td>
          <td :data-testid="`exclusions-table-description-col-${index}`">
            <ellipsified-copy-text v-if="item.description" width="300px" :text="item.description" />
          </td>
          <td class="coro-table-col--sticky-right">
            <v-menu location="bottom end">
              <template #activator="{ props }">
                <v-icon
                  :class="{ 'icon-btn-disabled': disableActions }"
                  icon="$dots"
                  v-bind="props"
                  :data-testid="`exclusions-table-actions-${index}`"
                />
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, actionIndex) in actions"
                  :key="actionIndex"
                  :value="actionIndex"
                  :data-testid="`exclusions-table-action-${action.actionKey}-${index}`"
                  @click="
                    onActionClick({ action: action.actionKey, callback: action.callback, item })
                  "
                >
                  <v-list-item-title>
                    {{ $t(`endpointDataGovernance.exclusionsTab.actions.${action.actionKey}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import TableWrapper from "@/components/TableWrapper.vue";
import { getFormattedDateTime } from "@/_helpers/date";
import { useDialogsStore } from "@/_store/dialogs.module";
import { useI18n } from "vue-i18n";
import type { EndpointDgExclusionItem } from "@/_store/endpoint-data-governance/exclusions.module";
import { EndpointDgExclusionType } from "@/constants/endpoint-data-governance";
import type { GenericCallback, Pagination } from "@/types";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import { confirmationDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: {
    EllipsifiedCopyText,
    TableWrapper,
  },
  props: {
    items: {
      type: Array as PropType<EndpointDgExclusionItem[]>,
      required: true,
    },
    actions: {
      type: Array as PropType<Array<{ actionKey: string; callback: GenericCallback }>>,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      default: () => ({ page: 0, pageSize: 25 }),
    },
    totalItems: {
      type: Number,
      required: true,
    },
    disableActions: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["page-changed"],
  setup(props, { emit }) {
    const dialogsStore = useDialogsStore();
    const i18n = useI18n();

    const iconMap: Record<string, string> = {
      [EndpointDgExclusionType.FILE]: "$fileEmpty",
      [EndpointDgExclusionType.FOLDER]: "$folder",
    };

    const headers = [
      {
        title: i18n.t("endpointDataGovernance.exclusionsTab.table.path"),
        key: "path",
        sortable: false,
      },
      {
        title: i18n.t("endpointDataGovernance.exclusionsTab.table.description"),
        key: "description",
        sortable: false,
      },
      {
        title: "",
        headerProps: { "aria-label": i18n.t("ariaLabels.actionsColumn") },
      },
    ];

    const getIcon = (type: string) => iconMap[type] ?? "$fileEmpty";

    const getDisplayPath = (item: EndpointDgExclusionItem) => {
      if (item.environmentVariableKey) {
        return `__${item.environmentVariableKey}__\\${item.path}`;
      }
      return item.path;
    };

    const onActionClick = (event: {
      action: string;
      callback: GenericCallback;
      item: EndpointDgExclusionItem;
    }) => {
      const dialogConfig = confirmationDialogsConfigConstructor({
        item: event.item,
        action: event.action,
        callback: event.callback,
        disable: props.disableActions,
      });
      dialogsStore.openDialog(dialogConfig);
    };

    const handleOptionsUpdate = (event: { page: number; itemsPerPage: number }) => {
      const newPage = event.page - 1;
      const newPageSize = event.itemsPerPage;
      if (newPage !== props.pagination.page || newPageSize !== props.pagination.pageSize) {
        emit("page-changed", { pageSize: newPageSize, page: newPage });
      }
    };

    return {
      headers,
      getIcon,
      getDisplayPath,
      getFormattedDateTime,
      onActionClick,
      handleOptionsUpdate,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-table__wrapper {
    max-height: calc(100vh - 380px) !important;
  }
}
</style>
